import { InvalidTimeUnitError } from '../errors';

export enum TimeUnit {
  ms = 'ms',
  millisecond = 'millisecond',
  milliseconds = 'milliseconds',
  s = 's',
  second = 'second',
  seconds = 'seconds',
  min = 'min',
  minute = 'minute',
  minutes = 'minutes',
  hr = 'hr',
  hour = 'hour',
  hours = 'hours',
  d = 'd',
  day = 'day',
  days = 'days',
  wk = 'wk',
  week = 'week',
  weeks = 'weeks',
  mo = 'mo',
  month = 'month',
  months = 'months',
  yr = 'yr',
  year = 'year',
  years = 'years'
}

export type TimeUnitsMillisAndSeconds =
  | TimeUnit.ms
  | TimeUnit.millisecond
  | TimeUnit.milliseconds
  | TimeUnit.s
  | TimeUnit.second
  | TimeUnit.seconds;

export enum DateTimeFormatBasic {
  custom = 'Custom',
  iso = 'ISO-8601',
  milliseconds = 'Unix (milliseconds)',
  seconds = 'Unix (seconds)',
  sql = 'SQL'
}

export type DateTimeFormatsMillisAndSecondsBasic =
  | DateTimeFormatBasic.milliseconds
  | DateTimeFormatBasic.seconds
  | TimeUnitsMillisAndSeconds;

export enum AddRemoveOperationBasic {
  adding = 'Adding',
  subtracting = 'Subtracting'
}

export enum DateSourceBasic {
  playbookData = 'Playbook Data',
  current = 'Current Time'
}

export class TimeUnits {
  public static readonly milliseconds = new TimeUnits(TimeUnit.ms, TimeUnit.millisecond, TimeUnit.milliseconds);
  public static readonly seconds = new TimeUnits(TimeUnit.s, TimeUnit.second, TimeUnit.seconds);
  public static readonly minutes = new TimeUnits(TimeUnit.min, TimeUnit.minute, TimeUnit.minutes);
  public static readonly hours = new TimeUnits(TimeUnit.hr, TimeUnit.hour, TimeUnit.hours);
  public static readonly days = new TimeUnits(TimeUnit.d, TimeUnit.day, TimeUnit.days);
  public static readonly weeks = new TimeUnits(TimeUnit.wk, TimeUnit.week, TimeUnit.weeks);
  public static readonly months = new TimeUnits(TimeUnit.mo, TimeUnit.month, TimeUnit.months);
  public static readonly years = new TimeUnits(TimeUnit.yr, TimeUnit.year, TimeUnit.years);

  private constructor(
    public readonly abbreviated: TimeUnit,
    public readonly singular: TimeUnit,
    public readonly plural: TimeUnit
  ) {}

  public toString(): string {
    return this.abbreviated;
  }

  public static parse(text: string): TimeUnits {
    const timeUnit = TimeUnits.tryParse(text);
    if (!timeUnit) throw new InvalidTimeUnitError(text);
    return timeUnit;
  }

  public static tryParse(text: string): TimeUnits | undefined {
    for (const value of Object.values(TimeUnits)) {
      if (TimeUnits.isTimeUnit(value)) {
        if (
          this.unitsEqual(value.abbreviated, text) ||
          this.unitsEqual(value.singular, text) ||
          this.unitsEqual(value.plural, text)
        ) {
          return value;
        }
      }
    }
    return undefined;
  }

  private static isTimeUnit(value: unknown): value is TimeUnits {
    const timeUnit = value as TimeUnits;
    return timeUnit && 'abbreviated' in timeUnit && 'singular' in timeUnit && 'plural' in timeUnit;
  }

  private static unitsEqual(unit1: string, unit2: string): boolean {
    return !!(unit1 && unit2 && unit1.toLowerCase() === unit2.toLowerCase());
  }
}
